import { Clickable, Col, Icon, Row, Spinner2, Text } from 'components';
import { useOutsideClick } from 'core';
import useTeamChannelUpdates from 'core/useTeamChannelUpdates';
import { Api, QUIZ_TYPE, canManageChannel, canManageHalfTime, theme } from 'lib';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IrreversableModal } from 'screens/Admin/components';
import QuizCard, { QUIZ_STATE } from './QuizCard';
import QuizListPanel from './QuizListPanel';
import QuizPlayModal from './QuizPlayModal';

// Cards rendered inline on the profile page. Above this, an admin gets a
// "See all" link that routes to /admin/quick-quiz for the full manage surface.
// Three is what Shoutouts uses, and 3 × 280 + gaps stays under the 936px
// profile container so the row doesn't overflow into Latest Posts.
const VISIBLE_DESKTOP_COUNT = 3;

// Admin-only heavy panels — keep them out of the bundle a fan downloads.
// React.lazy splits these into separate chunks fetched on first open.
const QuizForm = lazy(() => import('screens/Admin/screens/QuickQuiz/components/QuizForm'));
const BulkCreateForm = lazy(() =>
  import('screens/Admin/screens/QuickQuiz/components/BulkCreateForm')
);

const PAGE_LIMIT = 10;

// Sort order shown to viewers: live first (the only ones that drive engagement),
// then upcoming (so admins can preview), then completed / locked at the tail.
const STATE_RANK = {
  [QUIZ_STATE.READY]: 0,
  [QUIZ_STATE.COMING_UP]: 1,
  [QUIZ_STATE.COMPLETED]: 2,
  [QUIZ_STATE.LOCKED]: 3
};

const sortQuizzes = (quizzes) =>
  [...quizzes].sort((a, b) => {
    const ra = STATE_RANK[a.state] ?? 99;
    const rb = STATE_RANK[b.state] ?? 99;
    if (ra !== rb) return ra - rb;
    // Within the same state, earliest start first for upcoming, recent first for past.
    const ad = a.startDate ? new Date(a.startDate).getTime() : 0;
    const bd = b.startDate ? new Date(b.startDate).getTime() : 0;
    if (a.state === QUIZ_STATE.COMING_UP) return ad - bd;
    return bd - ad;
  });

// Skeleton card matching QuizCard's footprint so the row doesn't jump in height
// while quizzes load. Three is the default visible count on desktop / first slide on mobile.
const SkeletonCard = styled.div`
  width: ${(p) => (p.isMobile ? 240 : 280)}px;
  min-width: ${(p) => (p.isMobile ? 240 : 280)}px;
  min-height: 160px;
  background: linear-gradient(90deg, ${theme.GREY_10} 0%, #f7f9fb 50%, ${theme.GREY_10} 100%);
  background-size: 200% 100%;
  border-radius: 12px;
  border: 1px solid ${theme.GREY_20};
  animation: shimmer 1.4s linear infinite;
  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const EmptyCard = styled.button`
  width: ${(p) => (p.isMobile ? 240 : 280)}px;
  min-width: ${(p) => (p.isMobile ? 240 : 280)}px;
  min-height: 160px;
  border: 1.5px dashed ${theme.GREY_30};
  background: transparent;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: ${theme.GREY_60};
  transition: border-color 0.15s ease, color 0.15s ease;
  &:hover {
    border-color: ${theme.SECONDARY};
    color: ${theme.SECONDARY};
  }
`;

const AddMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border: 1px solid ${theme.GREY_20};
  padding: 6px;
  z-index: 20;
`;

const MenuItem = styled.button`
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-family: CeraPro;
  font-size: 14px;
  color: ${theme.GREY_80};
  &:hover {
    background: ${theme.GREY_10};
  }
`;

/**
 * QuickQuizRow — Profile-page section listing a team's Quick Quizzes.
 *
 * Visibility:
 *  - Renders only when the profile owner is a team (caller already gates on this).
 *  - For fans/anon: only currently-LIVE quizzes via the public-ish /quiz/list (auth required).
 *  - For admins (P4P or the team itself viewing own profile): the full admin list, including
 *    inactive and upcoming — so they can manage everything from this one surface.
 *
 * Side panels (QuizForm, BulkCreateForm) are lazy-loaded; fans never download them.
 *
 * `dailyOnly` (fan team profile): the half-time quiz now lives on the rotating Team Channel
 * surface, so the daily row drops half-time quizzes and shows only DAILY ones — keeping daily
 * quizzes a fan home without duplicating the half-time quiz in two places.
 */
const QuickQuizRow = ({ team, dailyOnly = false }) => {
  const currentUser = useSelector((state) => state.user);
  const isMobile = useSelector((state) => state.appState.isMobile);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const canManage = canManageChannel(currentUser, team?._id);
  const isP4PAdmin = canManageHalfTime(currentUser);
  const teamId = team?._id;

  // Per-quiz manage permission. Team admins can see HALF_TIME quizzes their
  // P4P admin created (so the list isn't mysteriously short), but they can't
  // edit them — only P4P admins manage half-time. Daily is fair game for both.
  const canManageQuiz = (quiz) =>
    canManage && (quiz.quizType === QUIZ_TYPE.DAILY || isP4PAdmin);

  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pagination via refs — Sidepanel's scroll handler is bound once on mount
  // and captures the closure of `onContentEndReached`. Refs let that closure
  // always read fresh values without us needing to re-bind on every render.
  const fetchingMoreRef = useRef(false);
  const endReachedRef = useRef(false);
  // Mirror of `quizzes.length` so the memoized fetch callback can read the
  // current count without re-creating on every state update.
  const quizzesCountRef = useRef(0);
  const [hasMore, setHasMore] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [formMode, setFormMode] = useState(null); // null | 'create' | 'edit'
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [playQuiz, setPlayQuiz] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [requireSignup, setRequireSignup] = useState(false);
  const [listPanelOpen, setListPanelOpen] = useState(false);

  const addMenuRef = useRef(null);
  useOutsideClick(addMenuRef, () => setAddMenuOpen(false));

  // Single fetch path that switches by role. Admin list endpoint returns every quiz
  // (active + inactive + future-dated) so the manager sees their full inventory.
  // Public endpoint only returns currently-live ones — exactly what the fan should see.
  //
  // `append` controls reset vs. paginate:
  //   - false (initial load / refresh) — replaces the array, resets pagination flags.
  //   - true  (infinite scroll)        — appends to the existing array.
  const fetchQuizzes = useCallback(
    async ({ append = false } = {}) => {
      if (!teamId) return;
      if (!currentUser) {
        setQuizzes([]);
        setLoading(false);
        return;
      }
      // Guard against duplicate in-flight or post-exhaustion fetches.
      if (append) {
        if (fetchingMoreRef.current || endReachedRef.current) return;
        fetchingMoreRef.current = true;
      } else {
        endReachedRef.current = false;
        setLoading(true);
      }

      const offset = append ? quizzesCountRef.current : 0;

      let nextChunk = [];
      let totalKnown = null;

      if (canManage) {
        const params = new URLSearchParams({
          teamId,
          limit: String(PAGE_LIMIT),
          offset: String(offset)
        });
        const res = await Api.get(`/admin/quizzes/list?${params.toString()}`);
        const data = Array.isArray(res?.data) ? res.data : [];
        // Normalize admin shape — add a synthetic `state` so QuizCard renders
        // consistently between admin and public lists.
        const now = Date.now();
        nextChunk = data.map((q) => {
          let state = QUIZ_STATE.READY;
          if (!q.isActive) state = QUIZ_STATE.LOCKED;
          else if (q.startDate && new Date(q.startDate).getTime() > now)
            state = QUIZ_STATE.COMING_UP;
          else if (q.endDate && new Date(q.endDate).getTime() < now)
            state = QUIZ_STATE.LOCKED;
          return { ...q, state };
        });
        totalKnown = res?.pagination?.total ?? null;
      } else {
        const res = await Api.get(
          `/quiz/list?teamId=${teamId}&limit=${PAGE_LIMIT}&offset=${offset}`
        );
        const list = Array.isArray(res?.quizzes) ? res.quizzes : [];
        // Fans only see what they can play right now. The backend already
        // date-filters to in-window quizzes, but half-time quizzes can still
        // come back as COMING_UP / LOCKED based on Sports-API game status —
        // strip those so fans don't see clutter on the team profile.
        nextChunk = list
          .filter((q) => q.state === QUIZ_STATE.READY || q.state === QUIZ_STATE.COMPLETED)
          // On the fan team profile the half-time quiz lives on the rotating surface, so the
          // daily row shows only DAILY quizzes (avoids showing the half-time quiz twice).
          .filter((q) => !dailyOnly || q.quizType === QUIZ_TYPE.DAILY)
          .map((q) => ({ ...q, isActive: true }));
        totalKnown = res?.pagination?.total ?? null;
      }

      // End-of-list detection: under-full page OR running total >= server total.
      const newTotal = (append ? quizzesCountRef.current : 0) + nextChunk.length;
      const reachedEnd =
        nextChunk.length < PAGE_LIMIT ||
        (totalKnown !== null && newTotal >= totalKnown);
      endReachedRef.current = reachedEnd;
      setHasMore(!reachedEnd);

      setQuizzes((prev) => {
        const next = sortQuizzes(append ? [...prev, ...nextChunk] : nextChunk);
        quizzesCountRef.current = next.length;
        return next;
      });

      if (append) {
        fetchingMoreRef.current = false;
      } else {
        setLoading(false);
      }
    },
    // `quizzes.length` is intentionally NOT a dependency — appending would
    // re-create the callback on every load and re-trigger the effect. We
    // read it via closure each call instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamId, canManage, currentUser, dailyOnly]
  );

  // Keep the count ref in sync with state. The fetch callback already updates
  // it inline; this catches deletes/inserts from elsewhere (optimistic toggle,
  // delete, saved-create refetch).
  useEffect(() => {
    quizzesCountRef.current = quizzes.length;
  }, [quizzes.length]);

  // Initial / role-change fetch — always a fresh reset, never an append.
  useEffect(() => {
    fetchQuizzes({ append: false });
  }, [fetchQuizzes]);

  const loadMore = useCallback(() => {
    return fetchQuizzes({ append: true });
  }, [fetchQuizzes]);

  // Realtime — the Team Channel surface is shared with Polls. Live-update on
  // TEAM_CHANNEL_UPDATE quiz events: patch deletes, debounce-refetch the rest.
  const rtTimerRef = useRef(null);
  const scheduleQuizRefetch = useCallback(() => {
    if (rtTimerRef.current) clearTimeout(rtTimerRef.current);
    rtTimerRef.current = setTimeout(() => fetchQuizzes({ append: false }), 350);
  }, [fetchQuizzes]);
  useEffect(() => () => clearTimeout(rtTimerRef.current), []);

  useTeamChannelUpdates(
    teamId,
    useCallback(
      (evt) => {
        if (!evt || evt.entity !== 'quiz') return;
        if (evt.action === 'deleted') {
          setQuizzes((prev) => prev.filter((q) => q._id !== evt.id));
          return;
        }
        // Per-answer results don't change the quiz cards here — ignore to avoid a
        // re-fetch storm on every answer. Structural changes still refresh the row.
        if (evt.action === 'results_updated') return;
        scheduleQuizRefetch();
      },
      [scheduleQuizRefetch]
    )
  );

  const handleCreate = () => {
    setAddMenuOpen(false);
    setEditingQuiz(null);
    setFormMode('create');
  };

  const handleBulk = () => {
    setAddMenuOpen(false);
    setBulkOpen(true);
  };

  const handleEdit = (quiz) => {
    setEditingQuiz(quiz);
    setFormMode('edit');
  };

  const handleDelete = (quiz) => setDeleteTarget(quiz);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const ok = await Api.delete(`/admin/quizzes/delete/${deleteTarget._id}`);
    setDeleting(false);
    if (ok) {
      setQuizzes((prev) => prev.filter((q) => q._id !== deleteTarget._id));
      dispatch({ type: 'HANDLE_RES', payload: { res: t('pollQuizProfile.quizDeleted') } });
      setDeleteTarget(null);
    }
  };

  // Optimistic toggle — patch state immediately, roll back on failure. We don't
  // silence the error path: an overlap conflict on reactivation should surface
  // as a toast so the admin knows to free the window.
  const handleToggleActive = useCallback(
    async (quiz) => {
      const next = !quiz.isActive;
      setQuizzes((prev) =>
        prev.map((q) => (q._id === quiz._id ? { ...q, isActive: next } : q))
      );
      const res = await Api.put('/admin/quizzes/update', { id: quiz._id, isActive: next });
      if (!res || (res && res.err)) {
        // Rollback
        setQuizzes((prev) =>
          prev.map((q) => (q._id === quiz._id ? { ...q, isActive: !next } : q))
        );
      } else if (res?.data) {
        setQuizzes((prev) =>
          prev.map((q) => (q._id === quiz._id ? { ...q, ...res.data, state: q.state } : q))
        );
      }
    },
    []
  );

  const handleSavedSingle = () => {
    fetchQuizzes();
    dispatch({ type: 'HANDLE_RES', payload: { res: t('pollQuizProfile.quizSaved') } });
    setFormMode(null);
    setEditingQuiz(null);
  };

  const handleSavedBulk = (count) => {
    fetchQuizzes();
    dispatch({
      type: 'HANDLE_RES',
      payload: { res: t('pollQuizProfile.quizzesCreated', { count }) }
    });
    setBulkOpen(false);
  };

  const handlePlay = (quiz) => {
    if (!currentUser) {
      setRequireSignup(true);
      setPlayQuiz(quiz);
      return;
    }
    setRequireSignup(false);
    setPlayQuiz(quiz);
  };

  const handleAnswered = (quizId, summary) => {
    // Patch the card in-place so the next render shows "Answered" without a refetch.
    setQuizzes((prev) =>
      prev.map((q) =>
        q._id === quizId
          ? { ...q, answered: true, state: QUIZ_STATE.COMPLETED, _result: summary }
          : q
      )
    );
  };

  // Desktop: cap at 3 cards so the row stays within the profile's 936px container.
  // Mobile: render everything — the row scrolls horizontally so overflow is fine.
  const visibleQuizzes = useMemo(
    () => (isMobile ? quizzes : quizzes.slice(0, VISIBLE_DESKTOP_COUNT)),
    [quizzes, isMobile]
  );
  const hiddenCount = Math.max(0, quizzes.length - VISIBLE_DESKTOP_COUNT);
  // Surface See all whenever the inline row is truncated OR the server has
  // more pages we haven't fetched yet — otherwise an exactly-3-card first page
  // with a larger total would hide the entry point to the rest.
  const showSeeAll = !isMobile && (hiddenCount > 0 || hasMore);

  // Hide the section entirely when there's nothing to render and the viewer
  // can't manage. We never want a fan to see an empty section with no purpose.
  if (!teamId) return null;
  if (!loading && !canManage && visibleQuizzes.length === 0) return null;
  // Anonymous viewer with no quizzes loaded: skip too. Logged-in admins still see
  // the empty-state CTA card so they can create their first quiz.
  if (!currentUser && !canManage) return null;

  return (
    <>
      <Col wid="100%" marg="0">
        {/* Title + Add as a tight group on the left; See all sits flush
            against the right edge (matching the Shoutouts header layout). */}
        <Row
          pad={isMobile ? '0 16px' : '0'}
          between
          center
          wid={isMobile ? 'calc(100% - 32px)' : '100%'}
        >
          <Row noFlex center gap="14px">
            <Text h2>{t('pollQuizProfile.quizTitle')}</Text>

            {canManage && (
              // Admin: + with a dropdown of create options. Same idiom as the
              // /admin/quick-quiz screen for muscle-memory.
              <div ref={addMenuRef} style={{ position: 'relative' }}>
                <Clickable onClick={() => setAddMenuOpen((v) => !v)}>
                  <Row center gap="6px">
                    <Icon type="add" dimensions={18} col={theme.SECONDARY} />
                    <Text b2 bold col={theme.SECONDARY}>
                      {t('pollQuizProfile.add')}
                    </Text>
                  </Row>
                </Clickable>

                {addMenuOpen && (
                  <AddMenu>
                    <MenuItem type="button" onClick={handleCreate}>
                      <Icon type="add" dimensions={16} col={theme.SECONDARY} />
                      <Col noFlex>
                        <Text med>{t('pollQuizProfile.createSingleQuiz')}</Text>
                        <Text c2 col={theme.GREY_60}>
                          {t('pollQuizProfile.createSingleQuizSub')}
                        </Text>
                      </Col>
                    </MenuItem>
                    <MenuItem type="button" onClick={handleBulk}>
                      <Icon type="calendar" dimensions={16} col={theme.SECONDARY} />
                      <Col noFlex>
                        <Text med>{t('pollQuizProfile.bulkCreate')}</Text>
                        <Text c2 col={theme.GREY_60}>
                          {t('pollQuizProfile.bulkCreateSub')}
                        </Text>
                      </Col>
                    </MenuItem>
                  </AddMenu>
                )}
              </div>
            )}
          </Row>

          {showSeeAll && (
            // Right-edge "See all" — opens the QuizListPanel sidepanel.
            // Mirrors the Shoutouts header so the two sections share visual rhythm.
            <Clickable onClick={() => setListPanelOpen(true)}>
              <Text b2 bold col={theme.GREY_70}>
                {t('pollQuizProfile.seeAllCount', { count: quizzes.length })}
              </Text>
            </Clickable>
          )}
        </Row>

        {/* Horizontal scroll on every viewport — when there are many quizzes
            (e.g. a bulk-created 30-day batch) we don't want the row to push
            the profile container wider than the rest of the page sections.
            Gap matches Shoutouts (24px desktop) so the two sections line up. */}
        <Row
          start
          wid={isMobile ? '100vw' : '100%'}
          scrollX
          marg={isMobile ? '12px 0 0' : '16px 0 0'}
          gap={isMobile ? '12px' : '24px'}
          pos="relative"
          style={{ scrollbarWidth: 'none' }}
        >
          {loading ? (
            <>
              {/* Mobile rows scroll horizontally; pad the first card so it lines up
                  with the section title (which has its own 16px left padding). */}
              {[0, 1, 2].map((i) => (
                <Row key={i} marg={isMobile && i === 0 ? '0 0 0 16px' : '0'}>
                  <SkeletonCard isMobile={isMobile} />
                </Row>
              ))}
            </>
          ) : (
            <>
              {/* Empty-state CTA card for admins — kicks off the create flow with one tap. */}
              {canManage && visibleQuizzes.length === 0 && (
                <Row marg={isMobile ? '0 0 0 16px' : '0'}>
                  <EmptyCard isMobile={isMobile} onClick={handleCreate}>
                    <Icon type="add" dimensions={24} col="currentColor" />
                    <Text med col="currentColor">
                      {t('pollQuizProfile.createFirstQuiz')}
                    </Text>
                    <Text c2 col={theme.GREY_50} center>
                      {t('pollQuizProfile.createFirstQuizSub')}
                    </Text>
                  </EmptyCard>
                </Row>
              )}

              {visibleQuizzes.map((q, i) => (
                <Row marg={isMobile && i === 0 ? '0 0 0 16px' : '0'} key={q._id}>
                  <QuizCard
                    quiz={q}
                    isMobile={isMobile}
                    canManage={canManageQuiz(q)}
                    onPlay={handlePlay}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleActive={handleToggleActive}
                  />
                </Row>
              ))}
            </>
          )}
        </Row>
      </Col>

      {/* LIST PANEL — slides in from the right. Stays mounted so the close
          animation runs even on the last close. Shares all parent handlers so
          delete / edit / toggle in the panel update the inline row too. */}
      <QuizListPanel
        visible={listPanelOpen}
        quizzes={quizzes}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={loadMore}
        canManageQuiz={canManageQuiz}
        onClose={() => setListPanelOpen(false)}
        onPlay={(q) => {
          // Closing the side panel keeps the right side clear for the play
          // modal to feel focused. The list stays in state — reopening shows
          // the same list with whatever optimistic updates landed.
          setListPanelOpen(false);
          handlePlay(q);
        }}
        onEdit={(q) => {
          setListPanelOpen(false);
          handleEdit(q);
        }}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
      />

      {/* PLAY MODAL — fan only. Anonymous viewers get a sign-in prompt. */}
      <QuizPlayModal
        visible={!!playQuiz}
        quiz={playQuiz}
        onClose={() => {
          setPlayQuiz(null);
          setRequireSignup(false);
        }}
        onAnswered={handleAnswered}
        requireSignup={requireSignup}
      />

      {/* ADMIN PANELS — mounted only when needed, lazy-imported. */}
      {canManage && (
        <Suspense fallback={null}>
          {(formMode || editingQuiz) && (
            <QuizForm
              visible={!!formMode}
              user={currentUser}
              teamId={teamId}
              mode={formMode || 'create'}
              quiz={editingQuiz}
              onClose={() => {
                setFormMode(null);
                setEditingQuiz(null);
              }}
              onSaved={handleSavedSingle}
            />
          )}

          {bulkOpen && (
            <BulkCreateForm
              visible={bulkOpen}
              teamId={teamId}
              existingQuizzes={quizzes}
              onClose={() => setBulkOpen(false)}
              onSaved={handleSavedBulk}
            />
          )}
        </Suspense>
      )}

      <IrreversableModal
        visible={!!deleteTarget}
        heading={t('pollQuizProfile.deleteQuizHeading')}
        topText={deleteTarget ? `"${deleteTarget.question}"` : ''}
        text={t('pollQuizProfile.deleteQuizText')}
        confirmText={deleting ? t('pollQuizProfile.deleting') : t('pollQuizProfile.delete')}
        waiting={deleting}
        onConfirm={confirmDelete}
        closeModal={() => !deleting && setDeleteTarget(null)}
        confirmNoClose
      />
    </>
  );
};

export default QuickQuizRow;

// Tiny helper to expose a shared loading pattern if Fan.js ever wants to wrap
// the row in its own spinner. Not strictly needed today.
export const QuickQuizLoading = () => (
  <Col centerAll pad="40px 0">
    <Spinner2 col={theme.SECONDARY} />
  </Col>
);
